import React from "react";

export const createStableRouteRenderer = () => {
  const renderers = new WeakMap();

  return (Tag) => {
    if (!renderers.has(Tag)) {
      const RouteRenderer = (props) => <Tag {...props} />;
      RouteRenderer.displayName = `RouteRenderer(${Tag.displayName || Tag.name || "Feature"})`;
      renderers.set(Tag, RouteRenderer);
    }
    return renderers.get(Tag);
  };
};

const readScalar = (source, name) => {
  const value = source?.[name];
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
};

/**
 * Identifiant d'entité porté par une route legacy, lu là où les vues V1 le
 * lisent elles-mêmes au montage : `location.state`, puis les paramètres de
 * chemin, puis la query string.
 */
export const readRouteEntityKey = ({ location, match } = {}, parameterNames = []) => {
  const query = new URLSearchParams(location?.search || "");
  return parameterNames
    .map((name) => (
      readScalar(location?.state, name)
      || readScalar(match?.params, name)
      || query.get(name)
      || ""
    ))
    .join("::");
};

/**
 * Variante de `createStableRouteRenderer` dont l'élément rendu est clé par
 * l'identifiant d'entité de la route.
 *
 * Les vues V1 de fiche (individu, enclos, secteur, campagne…) lisent leur
 * identifiant une seule fois, dans `componentDidMount`. Quand la recherche
 * globale ou un lien interne pousse la même route avec un autre identifiant,
 * React Router se contente de mettre à jour les props de l'instance déjà
 * montée : l'URL change, la page reste figée sur l'ancienne entité. Changer la
 * clé force un vrai remontage ; les autres paramètres (onglet, filtres) ne
 * remontent rien.
 */
export const createEntityRouteRenderer = () => {
  const renderers = new WeakMap();

  return (Tag, parameterNames) => {
    if (!Array.isArray(parameterNames) || parameterNames.length === 0) {
      throw new TypeError("createEntityRouteRenderer: parameterNames must list at least one entity parameter");
    }
    if (!renderers.has(Tag)) renderers.set(Tag, new Map());
    const byParameters = renderers.get(Tag);
    const signature = parameterNames.join("|");
    if (!byParameters.has(signature)) {
      const RouteRenderer = (props) => (
        <Tag key={readRouteEntityKey(props, parameterNames)} {...props} />
      );
      RouteRenderer.displayName = `EntityRouteRenderer(${Tag.displayName || Tag.name || "Feature"})`;
      byParameters.set(signature, RouteRenderer);
    }
    return byParameters.get(signature);
  };
};
